import React, { useMemo, useState, useRef, useEffect } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  getGroupedRowModel,
  getExpandedRowModel,
  getPaginationRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  flexRender,
  type ColumnFiltersState,
  type SortingState,
  type GroupingState,
  type ExpandedState,
  type VisibilityState,
  type ColumnOrderState,
  type PaginationState,
  type ColumnSizingState,
  type ColumnPinningState,
} from '@tanstack/react-table';
import { DataTableProps, DataTableColumn } from './types';
import DataTableHeader from './components/DataTableHeader';
import DataTableBody from './components/DataTableBody';
import ColumnVisibility from './components/ColumnVisibility';
import MultiSort from './components/MultiSort';
import GroupingControls from './components/GroupingControls';
import ExportButton from './components/ExportButton';
import { multiSelect } from './filters';

// Default minimum column width constant
const DEFAULT_MIN_COLUMN_WIDTH = 80;

// localStorage utility functions for column persistence
const getStoredColumnSettings = (tableId: string) => {
  try {
    const stored = localStorage.getItem(`datatable_${tableId}_columns`);
    return stored ? JSON.parse(stored) : null;
  } catch (error) {
    console.warn('Failed to parse stored column settings:', error);
    return null;
  }
};

const saveColumnSettings = (tableId: string, visibility: VisibilityState, order: string[], pinning?: ColumnPinningState) => {
  try {
    const settings = {
      visibility,
      order,
      pinning
    };
    localStorage.setItem(`datatable_${tableId}_columns`, JSON.stringify(settings));
  } catch (error) {
    console.warn('Failed to save column settings:', error);
  }
};

export function DataTable<TData extends Record<string, any>>({
  data,
  columns,
  enableFiltering = false,
  enableSorting = false,
  enableMultiSort = false,
  enableGrouping = false,
  enableColumnVisibility = false,
  enableColumnResizing = false,
  enableColumnPinning = false,
  enableMultiSelect = false,
  enableExport = false,
  stickyHeaders = false,
  enableVirtualization = false,
  virtualizationRowHeight = 52,
  virtualizationOverscan = 100,
  defaultColumnVisibility = {},
  onColumnVisibilityChange,
  defaultSorting = [],
  defaultColumnSizing = {},
  onColumnSizingChange,
  columnResizeMode = 'onChange',
  filterMode = 'internal',
  filterStyle = 'select',
  onFilterChange,
  onSortChange,
  groupBy = [],
  onGroupingChange,
  onExport,
  exportTransform,
  exportFilename,
  rowClickable = false,
  onRowClick,
  loading = false,
  emptyMessage = 'No data available',
  className = '',
  tableClassName = '',
  fullMinimumColumnWidth = DEFAULT_MIN_COLUMN_WIDTH,
  defaultFiltering,
  persistColumnSettings = false,
  tableId,
  groupingBarButtons,
  customToolbarButtons,
  useSteppedGroupingExpansion = false,
}: DataTableProps<TData>) {
  // Validate that tableId is provided when persistence is enabled
  if (persistColumnSettings && !tableId) {
    throw new Error('tableId is required when persistColumnSettings is enabled');
  }

  // Initialize state - either from localStorage or defaults
  const getInitialColumnSettings = () => {
    if (persistColumnSettings && tableId) {
      const stored = getStoredColumnSettings(tableId);
      if (stored) {
        return {
          visibility: stored.visibility || defaultColumnVisibility,
          order: stored.order || [],
          pinning: stored.pinning || { left: [], right: [] }
        };
      }
    }
    return {
      visibility: defaultColumnVisibility,
      order: [],
      pinning: { left: [], right: [] }
    };
  };

  const initialSettings = getInitialColumnSettings();

  const [rowSelection, setRowSelection] = React.useState({})
  const [sorting, setSorting] = useState<SortingState>(defaultSorting);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(defaultFiltering || []);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(initialSettings.visibility);
  const [columnOrder, setColumnOrder] = useState<ColumnOrderState>(initialSettings.order);
  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>(defaultColumnSizing);
  const [columnPinning, setColumnPinning] = useState<ColumnPinningState>(initialSettings.pinning);
  const [grouping, setGrouping] = useState<GroupingState>(groupBy);
  const [expanded, setExpanded] = useState<ExpandedState>({}); // Start with all groups collapsed
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const handleSortingChange = (updater: any) => {
    const newSorting = typeof updater === 'function' ? updater(sorting) : updater;
    setSorting(newSorting);
    onSortChange?.(newSorting);
  };

  const handleColumnFiltersChange = (updater: any) => {
    const newFilters = typeof updater === 'function' ? updater(columnFilters) : updater;
    if (filterMode === 'internal') {
      setColumnFilters(newFilters);
    }
    onFilterChange?.(newFilters);
  };

  const handleColumnVisibilityChange = (updater: any) => {
    const newVisibility = typeof updater === 'function' ? updater(columnVisibility) : updater;
    setColumnVisibility(newVisibility);
    onColumnVisibilityChange?.(newVisibility);
  };

  const handleGroupingChange = (updater: any) => {
    const newGrouping = typeof updater === 'function' ? updater(grouping) : updater;
    setGrouping(newGrouping);
    onGroupingChange?.(newGrouping);
  };

  const handleColumnSizingChange = (updater: any) => {
    const newSizing = typeof updater === 'function' ? updater(columnSizing) : updater;
    setColumnSizing(newSizing);
    onColumnSizingChange?.(newSizing);
  };

  const handleSaveColumnSettings = () => {
    if (persistColumnSettings && tableId) {
      saveColumnSettings(tableId, columnVisibility, columnOrder, columnPinning);
    }
  };

  const processedColumns = useMemo(() => {
    return columns.map(col => {
      const column = col as DataTableColumn<TData>;
      const processed = { ...column };
      if (column.enableFiltering !== false && enableFiltering) {
        processed.filterFn = column.filterFn || multiSelect;
      }

      if (column.sortingFn) {
        processed.sortingFn = column.sortingFn;
      }

      // Always add column sizing properties for CSS constraints
      if (column.size !== undefined) processed.size = column.size;
      // Apply minimum column width, using the larger of the column's minSize or the global minimum
      if (column.minSize !== undefined) {
        processed.minSize = Math.max(column.minSize, fullMinimumColumnWidth);
      } else {
        processed.minSize = fullMinimumColumnWidth;
      }
      if (column.maxSize !== undefined) processed.maxSize = column.maxSize;

      // Only add enableResizing if column resizing is enabled
      if (enableColumnResizing && column.enableResizing !== undefined) {
        processed.enableResizing = column.enableResizing;
      }

      return processed;
    });
  }, [columns, enableFiltering, enableColumnResizing]);

  const table = useReactTable({
    data,
    columns: processedColumns,
    state: {
      rowSelection,
      sorting,
      columnFilters: filterMode === 'internal' ? columnFilters : [],
      columnVisibility,
      columnOrder,
      columnSizing,
      columnPinning,
      grouping,
      expanded,
    },
    enableSorting,
    enableMultiSort,
    enableFilters: enableFiltering,
    enableGrouping,
    enableColumnResizing,
    enableColumnPinning,
    enableRowSelection: enableMultiSelect,
    onRowSelectionChange: setRowSelection,
    columnResizeMode: enableColumnResizing ? columnResizeMode : undefined,
    columnResizeDirection: 'ltr',
    defaultColumn: {
      size: 180,
      minSize: fullMinimumColumnWidth,
      maxSize: 600,
    },
    onSortingChange: handleSortingChange,
    onColumnFiltersChange: handleColumnFiltersChange,
    onColumnVisibilityChange: handleColumnVisibilityChange,
    onColumnOrderChange: setColumnOrder,
    onColumnSizingChange: handleColumnSizingChange,
    onColumnPinningChange: setColumnPinning,
    onGroupingChange: handleGroupingChange,
    onExpandedChange: setExpanded,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: enableFiltering ? getFilteredRowModel() : undefined,
    getSortedRowModel: enableSorting ? getSortedRowModel() : undefined,
    getGroupedRowModel: enableGrouping ? getGroupedRowModel() : undefined,
    getExpandedRowModel: enableGrouping ? getExpandedRowModel() : undefined,
    getFacetedRowModel: enableFiltering ? getFacetedRowModel() : undefined,
    getFacetedUniqueValues: enableFiltering ? getFacetedUniqueValues() : undefined,
  });

  const handleExport = () => {
    if (!onExport) return;

    const visibleRows = table.getFilteredRowModel().rows;
    const exportData = visibleRows.map(row => row.original);
    const transformedData = exportTransform ? exportTransform(exportData) : exportData;
    onExport(transformedData);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  return (
    <div className={`w-full ${className}`}>
      {(enableColumnVisibility || (enableSorting && enableMultiSort) || enableGrouping || enableExport || customToolbarButtons) && (
        <div className="mb-4 flex flex-col lg:flex-row lg:items-center gap-2">
          {enableColumnVisibility && (
            <ColumnVisibility
              table={table}
              enableUserControl={enableColumnVisibility}
              enableColumnPinning={enableColumnPinning}
              onClose={handleSaveColumnSettings}
            />
          )}
          {enableSorting && enableMultiSort && (
            <MultiSort table={table} />
          )}
          {enableGrouping && (
            <GroupingControls table={table} groupingBarButtons={groupingBarButtons} />
          )}
          {enableExport && (
            <ExportButton
              table={table}
              filename={exportFilename}
              onExport={onExport}
              exportTransform={exportTransform}
            />
          )}
          {customToolbarButtons && customToolbarButtons.map((buttonFn, index) => 
            buttonFn({
              setGrouping: handleGroupingChange,
              setColumnOrder: setColumnOrder,
              setColumnVisibility: handleColumnVisibilityChange,
              grouping,
              columnOrder,
              columnVisibility,
              table,
            })
          )}
        </div>
      )}
      <div
        ref={scrollContainerRef}
        className={`overflow-x-auto ${enableVirtualization ? 'overflow-y-auto max-h-[600px]' : stickyHeaders ? 'overflow-y-auto max-h-[600px]' : ''} border border-gray-200 rounded-lg ${tableClassName}`}
      >
        <table
          className={`min-w-full divide-y divide-gray-200`}
          style={enableColumnResizing ? { tableLayout: 'fixed', width: '100%' } : undefined}
        >
          <DataTableHeader
            table={table}
            enableSorting={enableSorting}
            enableMultiSort={enableMultiSort}
            enableFiltering={enableFiltering}
            enableGrouping={enableGrouping}
            enableColumnResizing={enableColumnResizing}
            filterStyle={filterStyle}
            stickyHeaders={stickyHeaders}
            useSteppedGroupingExpansion={useSteppedGroupingExpansion}
          />
          <DataTableBody
            table={table}
            columns={columns}
            emptyMessage={emptyMessage}
            enableVirtualization={enableVirtualization}
            enableColumnResizing={enableColumnResizing}
            rowHeight={virtualizationRowHeight}
            overscan={virtualizationOverscan}
            parentRef={enableVirtualization ? scrollContainerRef : undefined}
            rowClickable={rowClickable}
            onRowClick={onRowClick}
          />
        </table>
      </div>
    </div>
  );
}
